// Cookie consent and the analytics it gates. The choice lives in one cookie shared with the website (`fs_consent`,
// `v1.analytics` or `v1.necessary`, one year). Nothing analytic loads before a choice: PostHog (EU, no ads, no session
// recording) is injected only when the choice is "analytics" AND there is a project key (`VITE_POSTHOG_KEY`, else
// Filmsquare's own on its hosts; a self-hosted studio has none). Rejecting or withdrawing opts PostHog out and deletes
// what it stored (`ph_…` cookies and storage keys). What it records is kept to the free tier: pageviews, the product
// events of analytics.ts and uncaught errors; no autocapture, heatmaps, surveys or web vitals.
//
// Everything is pure or takes its browser pieces as one `ConsentEnv`, so the cookie, the gating and the cleanup are
// tested with fakes (there is no DOM in the test setup). The page uses `sharedConsent()`.

export const CONSENT_COOKIE = "fs_consent";
export const CONSENT_ANALYTICS = "v1.analytics";
export const CONSENT_NECESSARY = "v1.necessary";
export const CONSENT_MAX_AGE = 31_536_000;
export const COOKIE_POLICY_URL = "https://www.filmsquare.ai/cookies";
/** The website's domain: the cookie is shared across its subdomains (www, app). */
export const SITE_DOMAIN = "filmsquare.ai";
export const POSTHOG_DEFAULT_HOST = "https://eu.i.posthog.com";
export const POSTHOG_UI_HOST = "https://eu.posthog.com";
/** The public project key of Filmsquare's PostHog project (EU), used on the site's own hosts only. */
export const FILMSQUARE_POSTHOG_KEY = "phc_xB7uJanUe8Wz8vv3twHwBecjrL2Chop77xk6UHW7tZpf";
const PH_PREFIX = "ph_";

export type Consent = "analytics" | "necessary";

const VALUE: Record<Consent, string> = { analytics: CONSENT_ANALYTICS, necessary: CONSENT_NECESSARY };

/** The choice in a `document.cookie` string, or null when there is none (or a value of another version). */
export function parseConsent(cookies: string): Consent | null {
  for (const part of cookies.split(";")) {
    const at = part.indexOf("=");
    if (at < 0 || part.slice(0, at).trim() !== CONSENT_COOKIE) continue;
    let value = part.slice(at + 1).trim();
    try {
      value = decodeURIComponent(value);
    } catch {
      // keep the raw value; it will not match
    }
    if (value === CONSENT_ANALYTICS) return "analytics";
    if (value === CONSENT_NECESSARY) return "necessary";
  }
  return null;
}

/** `.filmsquare.ai` on the site's own hosts, else null (a host-only cookie, as on localhost or a self-hosted studio). */
export function cookieDomain(hostname: string): string | null {
  const h = hostname.toLowerCase();
  return h === SITE_DOMAIN || h.endsWith(`.${SITE_DOMAIN}`) ? `.${SITE_DOMAIN}` : null;
}

/** The string to assign to `document.cookie` to store a choice. */
export function consentCookie(choice: Consent, where: { hostname: string; secure: boolean }): string {
  const parts = [
    `${CONSENT_COOKIE}=${VALUE[choice]}`,
    "Path=/",
    `Max-Age=${CONSENT_MAX_AGE}`,
    "SameSite=Lax",
  ];
  if (where.secure) parts.push("Secure");
  const domain = cookieDomain(where.hostname);
  if (domain) parts.push(`Domain=${domain}`);
  return parts.join("; ");
}

/** The string that deletes a cookie of that name (host-only when `domain` is null). */
export function expiredCookie(name: string, domain: string | null): string {
  return `${name}=; Path=/; Max-Age=0${domain ? `; Domain=${domain}` : ""}`;
}

/** The names in a `document.cookie` string. */
export function cookieNames(cookies: string): string[] {
  return cookies
    .split(";")
    .map((p) => p.split("=")[0]?.trim() ?? "")
    .filter(Boolean);
}

/** Where PostHog's script comes from: the host's assets domain (`eu.i.posthog.com` → `eu-assets.i.posthog.com`). */
export function posthogScriptUrl(host: string = POSTHOG_DEFAULT_HOST): string {
  const base = host.replace(/\/+$/, "").replace(".i.posthog.com", "-assets.i.posthog.com");
  return `${base}/static/array.js`;
}

// ── the browser, as far as consent needs it ───────────────────────────────────────────────────────────────

interface ScriptLike {
  src: string;
  async: boolean;
  crossOrigin: string | null;
}
interface StorageLike {
  readonly length: number;
  key(index: number): string | null;
  removeItem(key: string): void;
}
export interface ConsentEnv {
  doc: {
    cookie: string;
    createElement(tag: "script"): ScriptLike;
    head: { appendChild(node: never): unknown };
  };
  location: {
    hostname: string;
    protocol: string;
    origin: string;
    pathname: string;
    search: string;
    hash: string;
  };
  /** localStorage and sessionStorage (a blocked one is left out) */
  storages: StorageLike[];
  win: {
    posthog?: unknown;
    addEventListener(type: "hashchange", fn: () => void): void;
    removeEventListener(type: "hashchange", fn: () => void): void;
  };
  /** the PostHog project key; without it nothing loads */
  key?: string;
  /** PostHog's ingest host; the EU cloud when absent */
  host?: string;
}

/** The part of PostHog the app calls (the snippet's stub queues these until the library has loaded). */
interface PostHogApi {
  init(key: string, config: Record<string, unknown>, name?: string): void;
  capture(event: string, properties?: Record<string, unknown>): void;
  identify(id: string): void;
  reset(): void;
  opt_out_capturing(): void;
  opt_in_capturing(): void;
}
type PostHogStub = unknown[] & PostHogApi & { _i: unknown[]; __SV: number };

const STUB_METHODS = ["capture", "identify", "reset", "opt_out_capturing", "opt_in_capturing"] as const;

/** The queueing stand-in PostHog's snippet puts on `window.posthog` until array.js has loaded and replays it. */
function createStub(): PostHogStub {
  const stub = [] as unknown[] as PostHogStub;
  stub._i = [];
  stub.__SV = 1;
  stub.init = (key, config, name) => void stub._i.push([key, config, name]);
  for (const m of STUB_METHODS) {
    (stub as unknown as Record<string, unknown>)[m] = (...args: unknown[]) => void stub.push([m, ...args]);
  }
  return stub;
}

// The website sends PostHog's shared cookie too, and cannot import this: apps/site/src/main.js has its own copy of these
// three patterns, redactUrl and redactEvent. Keep the two equal.

/** The token of a review link in an address (`#/s/<token>`), whatever follows it. */
const SHARE_TOKEN_IN_URL = /(#\/*s\/+)[^/?#&\s]+/g;

/** The token of a dailies unsubscribe link (`#/dailies/unsubscribe/<token>`), whatever follows it. */
const UNSUB_TOKEN_IN_URL = /#\/*dailies\/+unsubscribe(?:\/[^?#&\s]*)?/g;

/**
 * The value of a sign-in credential in a query or fragment, also when that address is url-encoded (once or more) inside
 * another value such as `redirect_to=`. Supabase's sign-in links carry the `*_token` names; `token` and `token_hash` are
 * its one-time email link, `code` an OAuth code (Higgsfield's way back). The name must be a whole param, so `error_code`
 * and `code_challenge` stay.
 */
const AUTH_PARAM_IN_URL =
  /((?:[?#&/]|%(?:25)*(?:3F|23|26|2F))(?:access_token|refresh_token|provider_token|provider_refresh_token|id_token|token_hash|code|token)(?:=|%(?:25)*3D))(?:(?!%(?:25)*(?:23|26))[^#&\s])+/gi;

/**
 * An address with every link credential removed: `…/#/s/<token>` reads `…/#/s/_`,
 * `…/#/dailies/unsubscribe/<token>` reads `…/#/dailies/unsubscribe`, and `…#access_token=<jwt>&type=signup` reads
 * `…#access_token=_&type=signup` (the same in a query, or encoded inside another param).
 */
export function redactUrl(url: string): string {
  return url
    .replace(SHARE_TOKEN_IN_URL, "$1_")
    .replace(UNSUB_TOKEN_IN_URL, "#/dailies/unsubscribe")
    .replace(AUTH_PARAM_IN_URL, "$1_");
}

/**
 * The address a pageview reports: no query string, in the address or after the `#`, so a link's secrets stay out;
 * a review link's token (`#/s/<token>`) is reported as `#/s/_`.
 */
export function pageUrl(loc: { origin: string; pathname: string; hash: string }): string {
  return redactUrl(`${loc.origin}${loc.pathname}${loc.hash.split("?")[0]}`);
}

interface CaptureEvent {
  properties?: Record<string, unknown>;
  $set?: Record<string, unknown>;
  $set_once?: Record<string, unknown>;
}

/**
 * PostHog's last look at every event before it leaves (`before_send`): the library itself adds the address
 * (`$current_url`, `$referrer`, `$initial_current_url`, …) to every event, exceptions included, so a review link's
 * token is redacted there too, not only in our own pageviews.
 */
export function redactEvent<E extends CaptureEvent | null>(event: E): E {
  if (!event) return event;
  for (const bag of [event.properties, event.$set, event.$set_once]) {
    if (!bag) continue;
    for (const [k, v] of Object.entries(bag)) if (typeof v === "string") bag[k] = redactUrl(v);
  }
  return event;
}

/** What the banner and the settings entry read. `focus` counts reopen requests (the banner takes focus on a new one). */
export interface ConsentState {
  choice: Consent | null;
  /** the banner is showing: no choice yet, or the person reopened it */
  open: boolean;
  focus: number;
}

export function createConsent(env: ConsentEnv) {
  const { doc, win, location } = env;
  let state: ConsentState = { choice: parseConsent(doc.cookie), open: false, focus: 0 };
  state.open = state.choice === null;
  const listeners = new Set<() => void>();
  let script: ScriptLike | null = null;
  let userId: string | null = null;
  let stopRoutes: (() => void) | null = null;
  let stopWaiting: (() => void) | null = null;
  let started = false;

  const set = (next: Partial<ConsentState>) => {
    state = { ...state, ...next };
    for (const l of [...listeners]) l();
  };
  const ph = () => win.posthog as PostHogApi | undefined;
  const isStub = () => Array.isArray(win.posthog);
  const active = () => script !== null && state.choice === "analytics";

  const pageview = () => {
    if (!active()) return;
    ph()?.capture("$pageview", { $current_url: pageUrl(location) });
  };

  // `search`, not `test`: the regex is global, and `test` would carry its position over to the next call
  const holdsCredential = () =>
    `${location.pathname}${location.search}${location.hash}`.search(AUTH_PARAM_IN_URL) >= 0;

  /**
   * PostHog keeps the raw address in its shared cookie as soon as it initialises, before any `before_send`, and the
   * website reads that cookie. So it waits until the address is clean: Supabase clears a sign-in link by setting
   * `location.hash`, which fires hashchange. An address nothing clears means no analytics for this page load.
   */
  function waitForCleanAddress() {
    if (stopWaiting) return;
    const retry = () => {
      if (holdsCredential()) return;
      stopWaiting?.();
      load();
    };
    win.addEventListener("hashchange", retry);
    stopWaiting = () => {
      win.removeEventListener("hashchange", retry);
      stopWaiting = null;
    };
  }

  function load() {
    if (!env.key || state.choice !== "analytics") return;
    if (holdsCredential()) return waitForCleanAddress();
    const config = {
      api_host: env.host || POSTHOG_DEFAULT_HOST,
      ui_host: POSTHOG_UI_HOST,
      person_profiles: "identified_only",
      disable_session_recording: true,
      persistence: "localStorage+cookie",
      cross_subdomain_cookie: true,
      capture_pageview: false,
      autocapture: false,
      capture_pageleave: false,
      capture_dead_clicks: false,
      enable_heatmaps: false,
      disable_surveys: true,
      capture_performance: false,
      capture_exceptions: true,
      before_send: redactEvent,
      // no feature flags here, and the flags request sends the stored first address past before_send
      advanced_disable_feature_flags: true,
      advanced_disable_feature_flags_on_first_load: true,
    };
    if (!script) {
      win.posthog ??= createStub();
      ph()?.init(env.key, config);
      const el = doc.createElement("script");
      el.async = true;
      el.crossOrigin = "anonymous";
      el.src = posthogScriptUrl(env.host || POSTHOG_DEFAULT_HOST);
      doc.head.appendChild(el as never);
      script = el;
    } else if (isStub()) {
      // withdrawn while the library was still loading (its queue was dropped): ask for the init again
      ph()?.init(env.key, config);
    } else {
      ph()?.opt_in_capturing();
    }
    if (!stopRoutes) {
      const onRoute = () => pageview();
      win.addEventListener("hashchange", onRoute);
      stopRoutes = () => win.removeEventListener("hashchange", onRoute);
    }
    pageview();
    if (userId) ph()?.identify(userId);
  }

  /** Delete PostHog's cookies (on the host and on the site's domain) and its `ph_…` storage keys. */
  function clean() {
    const domain = cookieDomain(location.hostname);
    for (const name of cookieNames(doc.cookie)) {
      if (!name.startsWith(PH_PREFIX)) continue;
      doc.cookie = expiredCookie(name, null);
      if (domain) doc.cookie = expiredCookie(name, domain);
    }
    for (const storage of env.storages) {
      try {
        for (let i = storage.length - 1; i >= 0; i--) {
          const k = storage.key(i);
          if (k?.startsWith(PH_PREFIX)) storage.removeItem(k);
        }
      } catch {
        // blocked storage holds nothing of ours
      }
    }
  }

  function withdraw() {
    stopWaiting?.();
    stopRoutes?.();
    stopRoutes = null;
    if (script && win.posthog) {
      if (isStub()) {
        // not loaded yet: drop what is queued so nothing is sent once it arrives
        const stub = win.posthog as PostHogStub;
        stub.length = 0;
        stub._i.length = 0;
      } else {
        try {
          ph()?.opt_out_capturing();
        } catch {
          // the cleanup below still runs
        }
      }
    }
    clean();
  }

  return {
    getState: () => state,
    subscribe(fn: () => void) {
      listeners.add(fn);
      return () => void listeners.delete(fn);
    },
    /** Once at start: with a standing "analytics" choice PostHog loads (when there is a key). Safe to call twice. */
    start() {
      if (started) return;
      started = true;
      load();
    },
    /** Store a choice and act on it: accept loads PostHog; reject (or a withdrawal) opts it out and cleans up. */
    choose(choice: Consent) {
      const domain = cookieDomain(location.hostname);
      // a host-only cookie from before would shadow the shared one
      if (domain) doc.cookie = expiredCookie(CONSENT_COOKIE, null);
      doc.cookie = consentCookie(choice, {
        hostname: location.hostname,
        secure: location.protocol === "https:",
      });
      const was = state.choice;
      started = true;
      set({ choice, open: false });
      if (choice === "analytics") {
        if (was !== "analytics" || !script) load(); // the same choice again changes nothing
      } else withdraw();
    },
    /** "Cookie settings": show the banner again. */
    reopen: () => set({ open: true, focus: state.focus + 1 }),
    /** Close a reopened banner without a new choice (with no choice yet it stays: nothing loads until one is made). */
    dismiss: () => state.choice !== null && set({ open: false }),
    /** A product event (analytics.ts); dropped without consent. */
    track(event: string, props?: Record<string, string>) {
      if (active()) ph()?.capture(event, props);
    },
    /** A person signed in: PostHog links their events to the user id (never the email). */
    identify(id: string) {
      userId = id;
      if (active()) ph()?.identify(id);
    },
    /** Signed out: PostHog forgets the person on this device. */
    reset() {
      userId = null;
      if (active()) ph()?.reset();
    },
  };
}

export type ConsentController = ReturnType<typeof createConsent>;

const blocked = <T>(get: () => T): T | null => {
  try {
    return get();
  } catch {
    return null;
  }
};

/** The real page's pieces. Only call it in a browser. */
export function browserEnv(): ConsentEnv {
  const storages = [blocked(() => window.localStorage), blocked(() => window.sessionStorage)].filter(
    (s): s is Storage => s !== null,
  );
  return {
    doc: document as unknown as ConsentEnv["doc"],
    location: window.location,
    storages,
    win: window as unknown as ConsentEnv["win"],
    key:
      import.meta.env.VITE_POSTHOG_KEY ||
      (cookieDomain(window.location.hostname) ? FILMSQUARE_POSTHOG_KEY : "") ||
      undefined,
    host: import.meta.env.VITE_POSTHOG_HOST || undefined,
  };
}

let shared: ConsentController | undefined;
/** The page's one consent controller. */
export function sharedConsent(): ConsentController {
  shared ??= createConsent(browserEnv());
  return shared;
}
